import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { fetchAssignedBugs, loadMoreAssignedBugs, updateBugStatus, updateBugNotes, resetAssignedBugs } from '../../store/slices/bugsAssignedSlice';
import AssignedBugDetailView from './components/AssignedBugDetailView';
import './BugsAssignedToMe.scss';

const BugsAssignedToMe = () => {
  const dispatch = useDispatch();
  const { bugs, loading, error, hasMore } = useSelector(state => state.bugsAssigned);
  const { user } = useSelector(state => state.auth);
  
  const [selectedBug, setSelectedBug] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    sensitivity: 'all',
    gameName: '',
    searchTerm: ''
  });
  
  // Stats calculation
  const [stats, setStats] = useState({
    totalBugs: 0,
    pendingBugs: 0,
    inProgressBugs: 0,
    resolvedBugs: 0,
    criticalBugs: 0,
    myBugs: 0
  });
  
  // Get unique game names for filter dropdown
  const uniqueGameNames = [...new Set(bugs.map(bug => bug.gameName))];
  
  useEffect(() => {
    // Reset bugs list on component mount
    dispatch(resetAssignedBugs());
    
    // Fetch initial bugs assigned to current user
    if (user?.email) {
      dispatch(fetchAssignedBugs({ assignedToEmail: user.email }));
    }
    
    // Clean up on unmount
    return () => {
      dispatch(resetAssignedBugs());
    };
  }, [dispatch, user?.email]);
  
  // Calculate stats when bugs change
  useEffect(() => {
    if (bugs && bugs.length > 0) {
      const inProgressBugs = bugs.filter(bug => bug.status === 'in_progress').length;
      const resolvedBugs = bugs.filter(bug => bug.status === 'resolved').length;
      const criticalBugs = bugs.filter(bug => bug.sensitivity === 'critical').length;
      const assignedBugs = bugs.filter(bug => bug.status === 'assigned').length;
      
      setStats({
        totalBugs: bugs.length,
        pendingBugs: assignedBugs, // Show assigned bugs as "pending work"
        inProgressBugs,
        resolvedBugs,
        criticalBugs,
        myBugs: bugs.length
      });
    } else {
      setStats({
        totalBugs: 0,
        pendingBugs: 0,
        inProgressBugs: 0,
        resolvedBugs: 0,
        criticalBugs: 0,
        myBugs: 0
      });
    }
  }, [bugs]);
  
  const loadMoreReportsHandler = async () => {
    if (loading || loadingMore || !hasMore || !user?.email) return;
    
    setLoadingMore(true);
    try {
      await dispatch(loadMoreAssignedBugs({ 
        assignedToEmail: user.email,
        filters 
      })).unwrap();
    } catch (err) {
      console.error('Failed to load more bugs:', err);
    } finally {
      setLoadingMore(false);
    }
  };
  
  const applyFilters = () => {
    if (!user?.email) return;
    dispatch(resetAssignedBugs());
    dispatch(fetchAssignedBugs({ assignedToEmail: user.email, filters }));
  };
  
  const resetFiltersHandler = () => {
    setFilters({
      status: 'all',
      sensitivity: 'all',
      gameName: '',
      searchTerm: ''
    });
    if (!user?.email) return;
    dispatch(resetAssignedBugs());
    dispatch(fetchAssignedBugs({ assignedToEmail: user.email }));
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };
  
  const viewBugDetails = (bug) => {
    setSelectedBug(bug);
    setShowDetailModal(true);
  };
  
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedBug(null);
  };
  
  const updateStatus = async (newStatus) => {
    if (!selectedBug) return;
    
    try {
      const result = await dispatch(updateBugStatus({
        bugId: selectedBug.id,
        status: newStatus,
        updatedBy: {
          name: user.displayName || user.email,
          email: user.email
        }
      })).unwrap();
      
      // Update the selectedBug state to reflect the status change immediately
      setSelectedBug({
        ...selectedBug,
        status: result.status,
        lastUpdated: result.lastUpdated,
        updatedBy: result.updatedBy
      });
      
    } catch (err) {
      console.error('Failed to update bug status:', err);
    }
  };
  
  const updateNotes = async (notes) => {
    if (!selectedBug) return;
    
    try {
      const result = await dispatch(updateBugNotes({
        bugId: selectedBug.id,
        notes,
        updatedBy: {
          name: user.displayName || user.email,
          email: user.email
        }
      })).unwrap();
      
      // Update the selectedBug state to reflect the notes change immediately
      setSelectedBug({
        ...selectedBug,
        notes: result.notes,
        lastUpdated: result.lastUpdated,
        updatedBy: result.updatedBy
      });
      
    } catch (err) {
      console.error('Failed to update bug notes:', err);
    }
  };
  
  // Filter bugs based on search term
  const filteredBugs = bugs.filter(bug => {
    if (filters.searchTerm && !bug.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) &&
        !bug.description.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });
  
  const getSensitivityBadge = (sensitivity) => {
    const sensitivityConfig = {
      low: { color: 'success', text: 'Low' },
      medium: { color: 'warning', text: 'Medium' },
      high: { color: 'danger', text: 'High' },
      critical: { color: 'critical', text: 'Critical' }
    };
    
    const config = sensitivityConfig[sensitivity] || { color: 'secondary', text: sensitivity };
    return <span className={`sensitivity-badge ${config.color}`}>{config.text}</span>;
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pending' },
      assigned: { color: 'info', text: 'Assigned' },
      in_progress: { color: 'primary', text: 'In Progress' },
      resolved: { color: 'success', text: 'Resolved' }
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      
      // Handle Firebase timestamp (object with seconds/nanoseconds)
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle Firebase timestamp already converted to Date
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle timestamp in milliseconds or seconds
      else if (typeof timestamp === 'number') {
        // If timestamp appears to be in seconds (less than current time in milliseconds)
        date = timestamp < 1e12 ? new Date(timestamp * 1000) : new Date(timestamp);
      }
      // Handle string timestamps
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else {
        return 'N/A';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };
  
  if (error) {
    return (
      <div className="bugs-assigned-container">
        <div className="error-message">
          <h3>Error Loading Assigned Bugs</h3>
          <p>{error}</p>
          <button onClick={() => user?.email && dispatch(fetchAssignedBugs({ assignedToEmail: user.email }))}>
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bugs-assigned-container">
      <div className="page-heading">
        <h2>Bugs Assigned to Me</h2>
        <p>Manage and resolve bugs assigned to you</p>
      </div>
      
      <div className="statistics-summary">
        <div className="stat-card">
          <h3>My Total Bugs</h3>
          <div className="stat-value">{stats.myBugs}</div>
        </div>
        <div className="stat-card">
          <h3>Pending Work</h3>
          <div className="stat-value">{stats.pendingBugs}</div>
        </div>
        <div className="stat-card">
          <h3>In Progress</h3>
          <div className="stat-value">{stats.inProgressBugs}</div>
        </div>
        <div className="stat-card">
          <h3>Resolved</h3>
          <div className="stat-value">{stats.resolvedBugs}</div>
        </div>
        <div className="stat-card">
          <h3>Critical</h3>
          <div className="stat-value">{stats.criticalBugs}</div>
        </div>
      </div>
      
      <div className="filters-section">
        <h3>Filter Assigned Bugs</h3>
        
        <div className="filters-row">
          <div className="filter-item">
            <label htmlFor="status">Status:</label>
            <select 
              id="status" 
              name="status" 
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="all">All Statuses</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          
          <div className="filter-item">
            <label htmlFor="sensitivity">Sensitivity:</label>
            <select 
              id="sensitivity" 
              name="sensitivity" 
              value={filters.sensitivity}
              onChange={handleFilterChange}
            >
              <option value="all">All Levels</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          
          <div className="filter-item">
            <label htmlFor="gameName">Game:</label>
            <select 
              id="gameName" 
              name="gameName" 
              value={filters.gameName}
              onChange={handleFilterChange}
            >
              <option value="">All Games</option>
              {uniqueGameNames.map((name, index) => (
                <option key={index} value={name}>{name}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-item">
            <label htmlFor="searchTerm">Search:</label>
            <input 
              type="text" 
              id="searchTerm" 
              name="searchTerm" 
              placeholder="Search bugs..." 
              value={filters.searchTerm}
              onChange={handleFilterChange}
            />
          </div>
          
          <button 
            className="filter-button" 
            onClick={applyFilters}
            disabled={loading}
          >
            Apply Filters
          </button>
          <button 
            className="reset-button" 
            onClick={resetFiltersHandler}
            disabled={loading}
          >
            Reset
          </button>
        </div>
      </div>
      
      <div className="bugs-table">
        {loading && bugs.length === 0 ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading assigned bugs...</p>
          </div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Game</th>
                  <th>Reporter</th>
                  <th>Assigned On</th>
                  <th>Status</th>
                  <th>Sensitivity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBugs.length > 0 ? (
                  filteredBugs.map(bug => (
                    <tr key={bug.id}>
                      <td className="bug-title">{bug.title}</td>
                      <td>{bug.gameName}</td>
                      <td>
                        <div className="reporter-info">
                          <span className="reporter-name">{bug.submittedBy?.name || 'N/A'}</span>
                          <span className="reporter-email">{bug.submittedBy?.email || 'N/A'}</span>
                        </div>
                      </td>
                      <td>{formatDate(bug.lastUpdated || bug.submittedAt)}</td>
                      <td>{getStatusBadge(bug.status)}</td>
                      <td>{getSensitivityBadge(bug.sensitivity)}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="view-button"
                            onClick={() => viewBugDetails(bug)}
                          >
                            View & Update
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="no-bugs">
                      {user?.email ? 'No bugs assigned to you. Check back later!' : 'Please log in to view assigned bugs.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {hasMore && bugs.length > 0 && (
              <div className="load-more">
                <button 
                  className="load-more-button"
                  onClick={loadMoreReportsHandler}
                  disabled={loading || loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner"></span>
                      Loading more...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
            
            {loadingMore && (
              <div className="loading-more">
                <div className="loading-spinner-small"></div>
                <span>Loading more bugs...</span>
              </div>
            )}
          </>
        )}
      </div>
      
      {showDetailModal && (
        <div className="modal-overlay">
          <AssignedBugDetailView 
            bug={selectedBug}
            onClose={closeDetailModal}
            onStatusUpdate={updateStatus}
            onNotesUpdate={updateNotes}
            currentUserEmail={user?.email}
          />
        </div>
      )}
    </div>
  );
};

export default BugsAssignedToMe;